import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, BadgeEuro, CheckCircle2, ChevronRight, ClipboardCheck, Lock, Mail, PackageCheck, ShieldAlert, ShieldCheck, ShoppingCart, Tags, Truck } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import {
  categoryLabel,
  commercialModeLabel,
  collectionPath,
  formatEuro,
  getRelatedProducts,
  matchConfidenceLabel,
  matchTypeLabel,
  productFitBullets,
  readinessBadges,
  type LiveDropProduct
} from '../data/liveDrop';
import { getProductLaunchContent } from '../data/productContent';
import { loadCartId, saveCartId } from '../lib/cartStorage';
import { addCartItem, createCart, formatApiPrice, getCheckoutCatalog, type CheckoutProduct } from '../lib/commerceApi';
import { trackEvent } from '../lib/analytics';

type ProductDetailPageProps = {
  product: LiveDropProduct;
};

function ProductHero({ product }: ProductDetailPageProps) {
  const content = getProductLaunchContent(product.sku);
  const secondaryHref = product.category === 'clubpakketten' ? '/#clubs' : '/keuzehulp/';
  const [checkoutProduct, setCheckoutProduct] = useState<CheckoutProduct | null>(null);
  const [checkoutMessage, setCheckoutMessage] = useState('Definitieve prijs, levering en bestelknop volgen bij livegang');
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    let isActive = true;
    getCheckoutCatalog()
      .then((catalog) => {
        if (!isActive) return;
        const match = catalog.products.find((item) => item.sku === product.sku) ?? null;
        setCheckoutProduct(match);
        if (match?.checkoutEligible) {
          setCheckoutMessage('Pilotvoorraad is server-side vrijgegeven voor checkout.');
        }
      })
      .catch(() => {
        if (isActive) setCheckoutMessage('Bestelstatus wordt later opgehaald; ontvang voorlopig een beschikbaarheidsupdate.');
      });
    return () => {
      isActive = false;
    };
  }, [product.sku]);

  async function handleAddToCart() {
    if (!checkoutProduct?.checkoutEligible) return;
    setIsAdding(true);
    try {
      let cartId = loadCartId();
      if (!cartId) {
        const cart = await createCart();
        cartId = cart.id;
        saveCartId(cart.id);
      }
      await addCartItem(cartId, product.sku, 1);
      trackEvent({ event: 'add_to_cart', location: 'product_detail', label: product.name, sku: product.sku, category: product.category, brand: product.brand, routeFamily: 'checkout' });
      window.location.href = '/winkelwagen/';
    } catch {
      setCheckoutMessage('Dit product is nu niet online bestelbaar. Ontvang een update zodra het klaarstaat.');
      trackEvent({ event: 'checkout_blocked', location: 'product_detail', label: 'add_to_cart_failed', sku: product.sku, category: product.category, brand: product.brand, routeFamily: 'checkout' });
    } finally {
      setIsAdding(false);
    }
  }

  const checkoutEligible = Boolean(checkoutProduct?.checkoutEligible);
  const displayPrice = checkoutProduct ? formatApiPrice(checkoutProduct.priceCents) : formatEuro(product.marketResearch.targetPriceInclVat);

  return (
    <section className="productHero">
      <figure className="productHeroVisualFrame">
        <img src={content.image} alt={content.imageAlt} loading="eager" />
        <figcaption>{content.imageDisclosure}</figcaption>
      </figure>

      <div className="productHeroCopy">
        <div className="productTitleBlock">
          <p className="eyebrow">{product.dropRole}</p>
          <h1>{product.name}</h1>
          <p className="productLead">{content.intro}</p>
        </div>

        <div className="productPills" aria-label="Productkenmerken">
          <span>{product.brand}</span>
          <span>{product.level}</span>
          <span>SKU {product.sku}</span>
          <span>{categoryLabel(product.category)}</span>
          <span>Eerste batch in voorbereiding</span>
        </div>

        <aside className="leadBuyPanel" aria-label="Prijsalert en readiness">
          <div className="leadBuyTop">
            <span>{checkoutEligible ? 'Prijs incl. btw' : 'Richtprijs incl. btw'}</span>
            <strong>{displayPrice}</strong>
            <small>{checkoutMessage}</small>
          </div>
          {checkoutEligible ? (
            <>
              <div className="leadBuyActions">
                <button className="primaryCta" type="button" disabled={isAdding} onClick={handleAddToCart}><ShoppingCart size={18} /> {isAdding ? 'Toevoegen...' : 'In winkelwagen'}</button>
                <a className="secondaryDarkCta" href="/winkelwagen/"><ShoppingCart size={18} /> Bekijk winkelwagen</a>
              </div>
              <p className="gateNote"><ShieldCheck size={16} /> Checkout is alleen beschikbaar na server-side prijs-, voorraad- en compliancecontrole.</p>
            </>
          ) : (
            <>
              <div className="leadBuyActions">
                <a className="primaryCta" href="/#nieuwsbrief" onClick={() => trackEvent({ event: 'cta_click', location: 'product_detail', label: content.primaryCta, sku: product.sku, category: product.category, brand: product.brand, matchType: product.marketResearch.benchmark.matchType, placement: 'lead_panel', routeFamily: 'product' })}><Mail size={18} /> {content.primaryCta}</a>
                <a className="secondaryDarkCta" href={secondaryHref} onClick={() => trackEvent({ event: 'cta_click', location: 'product_detail', label: content.secondaryCta, sku: product.sku, category: product.category, brand: product.brand, placement: 'lead_panel', routeFamily: 'product' })}><ClipboardCheck size={18} /> {content.secondaryCta}</a>
              </div>
              <p className="gateNote"><Lock size={16} /> Nog niet te bestellen. Laat je mailen zodra dit product klaarstaat voor verkoop of offerte.</p>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

function ProductCopyPanel({ product }: ProductDetailPageProps) {
  const content = getProductLaunchContent(product.sku);

  return (
    <section className="productPanel productCopyPanel" aria-labelledby="copy-heading">
      <div className="panelHeading">
        <Tags />
        <div>
          <p className="eyebrow">Productinformatie</p>
          <h2 id="copy-heading">{content.headline}</h2>
        </div>
      </div>
      <p className="productCopyIntro">{product.pagePitch}</p>
      <div className="copyBulletGrid">
        {content.bullets.map((bullet) => (
          <article key={bullet}>
            <CheckCircle2 size={18} />
            <p>{bullet}</p>
          </article>
        ))}
      </div>
      <div className="objectionBox">
        <strong>Goed om te weten</strong>
        <ul>
          {content.objections.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>
    </section>
  );
}

function PriceBenchmarkPanel({ product }: ProductDetailPageProps) {
  return (
    <section className="productPanel PriceBenchmarkPanel" aria-labelledby="benchmark-heading">
      <div className="panelHeading">
        <BadgeEuro />
        <div>
          <p className="eyebrow">Prijsindicatie</p>
          <h2 id="benchmark-heading">Duidelijk voordat je bestelt.</h2>
        </div>
      </div>
      <div className="benchmarkGrid">
        <article>
          <span>Richtprijs</span>
          <strong>{formatEuro(product.marketResearch.targetPriceInclVat)}</strong>
          <small>Incl. btw, nog geen publieke verkoopprijs</small>
        </article>
        <article>
          <span>Status</span>
          <strong>Volgt</strong>
          <small>Bestellen opent na prijs- en leveringscontrole</small>
        </article>
        <article>
          <span>Prijsbasis</span>
          <strong>{matchConfidenceLabel(product.marketResearch.benchmark.matchType)}</strong>
          <small>{matchTypeLabel(product.marketResearch.benchmark.matchType)}</small>
        </article>
      </div>
      <dl className="productSpecList">
        <div><dt>Prijsnotitie</dt><dd>Richtprijs; geen openbare prijsbelofte.</dd></div>
        <div><dt>Broncontrole</dt><dd>Gebaseerd op markt- en categorieprijzen die intern zijn gecontroleerd.</dd></div>
        <div><dt>Laatst bekeken</dt><dd>{product.marketResearch.checkedAt}</dd></div>
        <div><dt>Status</dt><dd>Prijsindicatie; bestellen opent later.</dd></div>
      </dl>
    </section>
  );
}

function antiFitBullets(product: LiveDropProduct): string[] {
  const out: string[] = [];
  const lvl = String(product.level ?? '').toLowerCase();
  if (lvl.includes('beginner') || lvl.includes('instap') || lvl.includes('recreatief')) {
    out.push('Je speelt al gevorderd en zoekt vooral maximale power of spin.');
  } else if (lvl.includes('gevorderd') || lvl.includes('competitie') || lvl.includes('pro')) {
    out.push('Dit wordt je allereerste paddle en je wilt vooral vergevingsgezindheid en controle.');
  }
  switch (product.category) {
    case 'paddles': out.push('Je zoekt liever een complete set met ballen en net dan een losse paddle.'); break;
    case 'ballen': out.push('Je speelt op een andere ondergrond (binnen vs buiten) dan waar deze bal voor bedoeld is.'); break;
    case 'startersets': out.push('Je wilt juist een specifieke losse paddle of merk zelf uitkiezen.'); break;
    case 'netten-en-belijning': out.push('Je speelt alleen op vaste banen en hebt geen eigen net nodig.'); break;
    default: break;
  }
  out.push('Twijfel je? Gebruik de keuzehulp voor een persoonlijk advies.');
  return out.slice(0, 3);
}

function ChoiceHelpPanel({ product }: ProductDetailPageProps) {
  const fitBullets = productFitBullets(product);
  const antiFit = antiFitBullets(product);

  return (
    <section className="productPanel ChoiceHelpPanel" aria-labelledby="choice-heading">
      <div className="panelHeading">
        <CheckCircle2 />
        <div>
          <p className="eyebrow">Keuzehulp</p>
          <h2 id="choice-heading">Past dit product bij deze speler?</h2>
        </div>
      </div>
      <div className="fitColumns">
        <div className="fitCol">
          <p className="fitColTitle fitYes"><CheckCircle2 size={16} /> Past bij jou als</p>
          <ul>
            {fitBullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
          </ul>
        </div>
        <div className="fitCol">
          <p className="fitColTitle fitNo"><ShieldAlert size={16} /> Minder geschikt als</p>
          <ul>
            {antiFit.map((bullet) => <li key={bullet}>{bullet}</li>)}
          </ul>
        </div>
      </div>
      <a className="secondaryDarkCta fullWidthCta" href="/keuzehulp/" onClick={() => trackEvent({ event: 'cta_click', location: 'product_detail', label: 'choice_help_keuzehulp', sku: product.sku, category: product.category, brand: product.brand, placement: 'choice_help', routeFamily: 'product' })}>
        <CheckCircle2 size={18} /> Doe de keuzehulp
      </a>
    </section>
  );
}

function ReadinessGatePanel({ product }: ProductDetailPageProps) {
  return (
    <section className="productPanel ReadinessGatePanel" aria-labelledby="readiness-heading">
      <div className="panelHeading">
        <ShieldAlert />
        <div>
          <p className="eyebrow">Beschikbaarheid</p>
          <h2 id="readiness-heading">Meld je aan voor de verkoopstart.</h2>
        </div>
      </div>
      <div className="readinessBadges">
        {readinessBadges(product).map((badge) => <span key={badge}>{badge}</span>)}
      </div>
      <dl className="productSpecList">
        <div><dt>Bestellen</dt><dd>Nog niet mogelijk.</dd></div>
        <div><dt>Volgende stap</dt><dd>{commercialModeLabel(product.commercialMode)}</dd></div>
        <div><dt>Beschikbaarheid</dt><dd>Eerste verkoopbatch wordt voorbereid.</dd></div>
        <div><dt>Update</dt><dd>Je ontvangt bericht zodra dit product live gaat.</dd></div>
        <div><dt>Levering</dt><dd>Levertijd en verzendkosten volgen bij verkoopstart.</dd></div>
      </dl>
    </section>
  );
}

function CheckoutFlowPanel({ product }: ProductDetailPageProps) {
  return (
    <section className="productPanel checkoutFlowPanel" aria-labelledby="checkout-flow-heading">
      <div className="panelHeading">
        <Lock />
        <div>
          <p className="eyebrow">Bestellen</p>
          <h2 id="checkout-flow-heading">Zo werkt bestellen straks.</h2>
        </div>
      </div>
      <ol className="checkoutSteps">
        <li><strong>Bekijk:</strong> check product, richtprijs en geschikt gebruik.</li>
        <li><strong>Volg:</strong> ontvang een update zodra de eerste verkoopbatch klaarstaat.</li>
        <li><strong>Bestel later:</strong> kopen opent pas met definitieve prijs, levering en retourinformatie.</li>
      </ol>
      <a className="secondaryDarkCta fullWidthCta" href="/#nieuwsbrief" onClick={() => trackEvent({ event: 'cta_click', location: 'checkout_flow_panel', label: 'launchalert_instead_of_checkout', sku: product.sku, category: product.category, brand: product.brand, placement: 'checkout_flow', routeFamily: 'product' })}>
        <Mail size={18} /> Ontvang bericht bij beschikbaarheid
      </a>
    </section>
  );
}

function SupplierGatePanel({ product }: ProductDetailPageProps) {
  return (
    <section className="productPanel supplierGatePanel" aria-labelledby="supplier-heading">
      <div className="panelHeading">
        <Truck />
        <div>
          <p className="eyebrow">Productinformatie</p>
          <h2 id="supplier-heading">Wat we voor verkoop nog afronden.</h2>
        </div>
      </div>
      <p>We ronden de definitieve productinformatie, beschikbaarheid en servicevoorwaarden af voordat dit product bestelbaar wordt. Zo weet je vooraf wat je koopt en wat je kunt verwachten.</p>
      <ul className="blockerList">
        <li>Definitieve productspecificaties en foto's.</li>
        <li>Heldere levertijd en verzendkosten.</li>
        <li>Retour- en garantieinformatie per producttype.</li>
      </ul>
      <p className="gateNote"><ShieldCheck size={16} /> Pas daarna openen we bestellen of offerte-aanvraag voor dit product.</p>
    </section>
  );
}

function RelatedProducts({ product }: ProductDetailPageProps) {
  const related = getRelatedProducts(product);

  return (
    <section className="productPanel RelatedProducts" aria-labelledby="related-heading">
      <div className="panelHeading">
        <PackageCheck />
        <div>
          <p className="eyebrow">Bundelroute</p>
          <h2 id="related-heading">Logische vervolgartikelen uit de collectie.</h2>
        </div>
      </div>
      <div className="relatedGrid">
        {related.map((relatedProduct) => (
          <a
            href={relatedProduct.pdpPath}
            key={relatedProduct.sku}
            onClick={() => trackEvent({ event: 'select_item', location: 'product_detail', label: relatedProduct.name, sku: relatedProduct.sku, category: relatedProduct.category, brand: relatedProduct.brand, placement: 'related_products', routeFamily: 'product' })}
          >
            <span>{categoryLabel(relatedProduct.category)}</span>
            <strong>{relatedProduct.name}</strong>
            <small>{formatEuro(relatedProduct.marketResearch.targetPriceInclVat)} richtprijs</small>
            <ArrowRight size={16} />
          </a>
        ))}
      </div>
    </section>
  );
}

export function ProductDetailPage({ product }: ProductDetailPageProps) {
  useEffect(() => {
    document.title = `${product.name} | ProPickleball.nl`;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content = `${product.name}: bekijk prijsindicatie, keuzehulp en beschikbaarheidsupdate bij ProPickleball.nl.`;
    }
    trackEvent({ event: 'view_item', location: 'product_detail', label: product.name, sku: product.sku, category: product.category, brand: product.brand, matchType: product.marketResearch.benchmark.matchType, routeFamily: 'product' });
    trackEvent({ event: 'supplier_gate_view', location: 'product_detail', label: product.supplierName, sku: product.sku, category: product.category, brand: product.brand, routeFamily: 'product' });
  }, [product]);

  return (
    <>
      <main className="productPage">
        <nav className="productTopbar" aria-label="Productnavigatie">
          <BrandLogo compact />
          <a className="backHome" href="/#live-drop"><ArrowLeft size={18} /> Terug naar collectie</a>
        </nav>
        <nav className="seoBreadcrumbs productBreadcrumbs" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <ChevronRight size={15} aria-hidden="true" />
          <a href="/producten/">Producten</a>
          <ChevronRight size={15} aria-hidden="true" />
          <a href={collectionPath(product.category)}>{categoryLabel(product.category)}</a>
          <ChevronRight size={15} aria-hidden="true" />
          <span>{product.name}</span>
        </nav>

        <ProductHero product={product} />

        <section className="productContentShell" aria-label="Productinformatie">
          <div className="productMainStack">
            <ProductCopyPanel product={product} />
            <ChoiceHelpPanel product={product} />
            <PriceBenchmarkPanel product={product} />
            <RelatedProducts product={product} />
          </div>
          <aside className="productSideStack" aria-label="Beschikbaarheid en productinformatie">
            <ReadinessGatePanel product={product} />
            <CheckoutFlowPanel product={product} />
            <SupplierGatePanel product={product} />
          </aside>
        </section>
      </main>
      <LegalFooter />
    </>
  );
}
